/**
 * TemplateSavePopover Component
 *
 * A small popover for entering a template name when saving.
 * Triggered by "Save" button on template sections.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Save, X, Loader2 } from 'lucide-react';

interface TemplateSavePopoverProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
  defaultName?: string;
  placeholder?: string;
  title?: string;
}

export const TemplateSavePopover: React.FC<TemplateSavePopoverProps> = ({
  isOpen,
  onClose,
  onSave,
  defaultName = '',
  placeholder = 'Template name...',
  title = 'Save Template',
}) => {
  const [name, setName] = useState(defaultName);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isOpen]);

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setName(defaultName);
      setError(null);
      setIsSaving(false);
    }
  }, [isOpen, defaultName]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Handle save
  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('Please enter a name');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(trimmedName);
      onClose();
    } catch (err) {
      setError('Failed to save template');
    } finally {
      setIsSaving(false);
    }
  }, [name, onSave, onClose]);

  // Handle key events
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }, [handleSave, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={popoverRef}
      className="absolute z-50 top-full left-0 mt-1 p-3 bg-gradient-to-br from-dark-surface/95 to-dark-bg/85 backdrop-blur-sm border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 min-w-[240px]"
    >
      {/* Title */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-white">{title}</span>
        <button
          onClick={onClose}
          className="p-1 text-dark-muted hover:text-white rounded transition-all duration-150"
        >
          <X className="w-3 h-3" />
        </button>
      </div>

      {/* Input */}
      <div className="space-y-2">
        <input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full px-2.5 py-1.5 bg-dark-bg border border-dark-border/30 rounded text-sm text-white placeholder:text-dark-muted focus:outline-none focus:border-purple-500"
          maxLength={50}
          disabled={isSaving}
        />

        {/* Error */}
        {error && (
          <p className="text-xs text-red-400">{error}</p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleSave}
            disabled={isSaving || !name.trim()}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 bg-purple-500 text-white rounded text-xs font-medium hover:bg-purple-600 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-3 h-3" />
                Save
              </>
            )}
          </button>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-3 py-1.5 text-dark-muted hover:text-white text-xs transition-all duration-150"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateSavePopover;
